import { Entity, IEntity, filterNull } from '@lycoris-nubila/ngx-lycoris';
import { data } from 'autoprefixer';
import { first, isEmpty } from 'lodash-es';
import * as moment from 'moment';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { Contact, IContact } from 'contact/contact';
import { IJobOffer, JobOffer } from 'job/job-offer';
import { IModule, Module } from 'module/module';
import { TaskType } from 'task/model/task.type';
import { IUser, User } from 'user/user';

export interface ITask extends IEntity {
	readonly done: boolean;
	readonly type: TaskType;
	readonly dueDate: Date;
	readonly ownerId: string;
	readonly modulesId: string[];
	readonly todoTitle: string;
	readonly contactId: string;
	readonly jobOfferId: string;
	readonly participantsId: string[];
	readonly todoDescription: string;
	readonly firstModule$: Observable<IModule>;
	readonly modulesName$: Observable<string>;
	readonly contact$: Observable<IContact>;
	readonly jobOffer$: Observable<IJobOffer>;
	readonly participants$: Observable<IUser[]>;
	readonly pastDue: boolean;
	readonly description: string;
	readonly isForModule: boolean;
	readonly externalEventId: string;
	readonly externalEventTitle: string;
	readonly isNotExternalTask: boolean;
	readonly typeCssIconClass: string;
	readonly statusCssIconClass: string;
	readonly modulesData$: Observable<TaskModule[]>;
}

export class Task extends Entity implements ITask {
	private _done: boolean;
	private _type: TaskType;
	private _dueDate: Date;
	private _ownerId: string;
	private _modulesId: string[];
	private _todoTitle: string;
	private _contactId: string;
	private _jobOfferId: string;
	private _participantsId: string[];
	private _todoDescription: string;
	private _modulesName$: Observable<string>;
	private _firstModule$: Observable<Module>;
	private _contact$: Observable<Contact>;
	private _participants$: Observable<User[]>;
	private _jobOffer$: Observable<JobOffer>;
	private _pastDue: boolean;
	private _description: string;
	private _isForModule: boolean;
	private _externalEventTitle: string;
	private _externalEventId: string;
	private _typeCssIconClass: string;
	private _statusCssIconClass: string;
	private _modulesData$: Observable<TaskModule[]>;

	constructor(
		id: string,
		done: boolean,
		type: TaskType,
		dueDate: Date,
		ownerId: string,
		modulesId: string[],
		todoTitle: string,
		contactId: string,
		jobOfferId: string,
		participantsId: string[],
		todoDescription: string,
		updateTimestamp: number,
		externalEventId: string,
		externalEventTitle: string,
		modules$: Observable<Module[]> = of(null),
		contact$: Observable<Contact> = of(null),
		participants$: Observable<User[]> = of([]),
		jobOffer$: Observable<JobOffer> = of(null)
	) {
		super(id, null, updateTimestamp);
		this._done = done;
		this._type = type;
		this._dueDate = dueDate;
		this._ownerId = ownerId;
		this._modulesId = modulesId;
		this._todoTitle = todoTitle;
		this._contactId = contactId;
		this._jobOfferId = jobOfferId;
		this._participantsId = participantsId;
		this._todoDescription = todoDescription;
		this._externalEventId = externalEventId;
		this._externalEventTitle = externalEventTitle;
		this._contact$ = contact$;
		this._jobOffer$ = jobOffer$;
		this._participants$ = participants$;

		this._pastDue = moment(dueDate)
			.tz('UTC')
			.isBefore(moment.tz('UTC'), 'minutes');

		if (done) {
			this._statusCssIconClass = 'fak fa-str-task-check p-color-positive';
		} else if (this._pastDue) {
			this._statusCssIconClass = 'fak fa-str-task-exclamation p-color-negative';
		} else {
			this._statusCssIconClass = 'fak fa-str-task-clock p-color-primary';
		}

		switch (type) {
			case 'CONTACT':
				this._typeCssIconClass = 'fak fa-str-users-link';
				this._description = 'presentation.app.common.task.contact.description';
				this._isForModule = false;
				break;
			case 'MEETING':
				this._typeCssIconClass = 'fak fa-str-users-link';
				this._description = isEmpty(this._modulesId)
					? 'presentation.app.common.task.meeting.description.withoutModule'
					: 'presentation.app.common.task.meeting.description.withModule';
				this._isForModule = false;
				break;
			case 'JOB_OFFER':
				this._description = 'presentation.app.common.task.job-offer.description';
				this._typeCssIconClass = 'fak fa-str-calendar-check-circle';
				this._isForModule = false;
				break;
			case 'TODO':
				this._typeCssIconClass = 'fak fa-str-thumbtack';
				this._description = this._todoDescription || this._todoTitle;
				this._isForModule = false;
				break;
			case 'EXTERNAL_EVENT':
				this._typeCssIconClass = 'fak fa fa-calendar';
				this._description = 'presentation.app.common.task.external.description';
				this._isForModule = false;
				break;
			case 'MODULE':
				this._typeCssIconClass = 'fak fa-str-cube';
				this._description = 'presentation.app.common.task.module.description';
				this._isForModule = true;
				break;
		}

		this._firstModule$ = modules$.pipe(filterNull(), map(first));
		this._modulesName$ = modules$.pipe(
			filterNull(),
			switchMap((modules) =>
				combineLatest(
					modules.map((module) => {
						return module.localization$.pipe(map((loc) => loc.name));
					})
				).pipe(map((val) => val.join(', ')))
			)
		);

		this._modulesData$ = modules$.pipe(
			filterNull(),
			switchMap((modules) =>
				combineLatest(
					modules.map((module) => {
						return module.localization$.pipe(
							map((loc) => ({ name: loc.name, id: module.id } as TaskModule))
						);
					})
				)
			)
		);
	}

	public get done(): boolean {
		return this._done;
	}

	public get isForModule(): boolean {
		return this._isForModule;
	}

	public get type(): TaskType {
		return this._type;
	}

	public get dueDate(): Date {
		return this._dueDate;
	}

	public get ownerId(): string {
		return this._ownerId;
	}

	public get modulesId(): string[] {
		return this._modulesId;
	}

	public get todoTitle(): string {
		return this._todoTitle;
	}

	public get contactId(): string {
		return this._contactId;
	}

	public get jobOfferId(): string {
		return this._jobOfferId;
	}

	public get participantsId(): string[] {
		return this._participantsId;
	}

	public get todoDescription(): string {
		return this._todoDescription;
	}

	public get firstModule$(): Observable<Module> {
		return this._firstModule$;
	}

	public get modulesName$(): Observable<string> {
		return this._modulesName$;
	}

	public get contact$(): Observable<Contact> {
		return this._contact$;
	}

	public get jobOffer$(): Observable<JobOffer> {
		return this._jobOffer$;
	}

	public get participants$(): Observable<User[]> {
		return this._participants$;
	}

	public get pastDue(): boolean {
		return this._pastDue;
	}

	public get description(): string {
		return this._description;
	}

	public get typeCssIconClass(): string {
		return this._typeCssIconClass;
	}

	public get statusCssIconClass(): string {
		return this._statusCssIconClass;
	}

	public get modulesData$(): Observable<TaskModule[]> {
		return this._modulesData$;
	}

	public get externalEventId(): string {
		return this._externalEventId;
	}

	public get externalEventTitle(): string {
		return this._externalEventTitle;
	}
	public get isNotExternalTask(): boolean {
		return this._type !== 'EXTERNAL_EVENT';
	}
}

export interface TaskModule {
	readonly name: string;
	readonly id: string;
}
